import { useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { KeyRound, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";

/**
 * Diálogo para o entregador (ou qualquer usuário) alterar sua própria senha
 * a partir do app mobile. Usa POST /api/auth/change-password que exige
 * senha atual + nova senha (mínimo 4 caracteres, checado no backend).
 */
export function MobileChangePasswordDialog({ open, onOpenChange }) {
  const [f, setF] = useState({ atual: "", nova: "", confirmar: "" });
  const [show, setShow] = useState({ atual: false, nova: false, confirmar: false });
  const [loading, setLoading] = useState(false);

  const reset = () => { setF({ atual: "", nova: "", confirmar: "" }); setShow({ atual: false, nova: false, confirmar: false }); };

  const submit = async () => {
    if (!f.atual || !f.nova || !f.confirmar) return toast.error("Preencha todos os campos");
    if (f.nova.length < 4) return toast.error("Nova senha muito curta (mínimo 4 caracteres)");
    if (f.nova !== f.confirmar) return toast.error("A confirmação não confere com a nova senha");
    if (f.nova === f.atual) return toast.error("A nova senha deve ser diferente da atual");
    setLoading(true);
    try {
      await api.post("/auth/change-password", { senha_atual: f.atual, nova_senha: f.nova });
      toast.success("Senha alterada com sucesso");
      reset();
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao alterar senha");
    } finally {
      setLoading(false);
    }
  };

  const field = (key, label, testid) => (
    <div>
      <Label className="text-xs">{label}</Label>
      <div className="relative">
        <Input
          type={show[key] ? "text" : "password"}
          value={f[key]}
          onChange={(e) => setF({ ...f, [key]: e.target.value })}
          data-testid={testid}
          className="pr-10"
          autoComplete={key === "atual" ? "current-password" : "new-password"}
        />
        <button type="button" onClick={() => setShow({ ...show, [key]: !show[key] })}
          className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
          data-testid={`${testid}-toggle`}
          aria-label={show[key] ? "Ocultar" : "Mostrar"}>
          {show[key] ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
        </button>
      </div>
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={(o) => { onOpenChange(o); if (!o) reset(); }}>
      <DialogContent className="max-w-sm" data-testid="mobile-change-password-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-orange-700">
            <KeyRound className="w-5 h-5" />Alterar minha senha
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          {field("atual", "Senha atual", "mcp-atual")}
          {field("nova", "Nova senha", "mcp-nova")}
          {field("confirmar", "Confirmar nova senha", "mcp-confirmar")}
          <div className="text-[11px] text-muted-foreground">Mínimo de 4 caracteres.</div>
          <div className="flex gap-2 pt-1">
            <Button variant="ghost" className="flex-1" onClick={() => { reset(); onOpenChange(false); }} disabled={loading}>Cancelar</Button>
            <Button className="flex-1 bg-orange-600 hover:bg-orange-700" onClick={submit} disabled={loading} data-testid="mcp-submit">
              {loading ? "Salvando..." : "Alterar"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
